import * as React from "react"
import {
  Button,
  Heading,
  StyledPanelBodySection,
  StyledPanelBoilerplate,
  ThemeCss,
} from "gatsby-interface"

import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.json"
import { RemoveDomainForm } from "./RemoveDomainForm"

import { GatsbyCloudCdnFeatures } from "./GatsbyCloudCdnFeatures"
import { HostingDetails, HostingDomain } from "@modules/graphql/types"
import { AddDomainForm } from "./AddDomainForm"
import { PrimarySecondaryCombo } from "./PrimarySecondaryCombo"
import { MakeDomainPrimaryForm } from "./MakeDomainPrimaryForm"
import { RequestCertificateForm } from "./RequestCertificateForm"
import { useTracker, SegmentEventType } from "@modules/analytics"

const headerCss: ThemeCss = theme => ({
  alignItems: `center`,
  borderBottom: `1px solid ${theme.colors.grey[20]}`,
  display: `flex`,
  justifyContent: `space-between`,
  paddingBottom: theme.space[2],
  marginTop: theme.space[11],
})

const headingCss: ThemeCss = theme => ({
  fontFamily: theme.fonts.body,
  fontSize: theme.fontSizes[2],
  fontWeight: theme.fontWeights.semiBold,
})

const listCss: ThemeCss = theme => ({
  margin: 0,
  marginTop: theme.space[5],
  listStyleType: `none`,
})

const formDescriptionCss: ThemeCss = theme => ({
  marginTop: theme.space[3],
  marginBottom: theme.space[6],
})

export type ConnectedDomainsProps = {
  hostingDetails: HostingDetails
  siteId: string
}

export function ConnectedDomains({
  hostingDetails,
  siteId,
  ...rest
}: ConnectedDomainsProps) {
  const { trackSegment } = useTracker()
  const [showDomainForm, setShowDomainForm] = React.useState<boolean>(false)
  const [domainToRemove, setDomainToRemove] = React.useState<string | null>(
    null
  )
  const [domainToPrimary, setDomainToPrimary] = React.useState<string | null>(
    null
  )
  const [
    manageCertificateForDomain,
    setManageCertificateForDomain,
  ] = React.useState<string | null>(null)

  const domains = hostingDetails.domains ?? []

  const { primaryDomains, secondaryDomains } = domains.reduce<{
    primaryDomains: HostingDomain[]
    secondaryDomains: Record<string, HostingDomain>
  }>(
    (acc, cur) => {
      if (cur.primaryDomainId) {
        acc.secondaryDomains[cur.primaryDomainId] = cur
      } else {
        acc.primaryDomains.push(cur)
      }

      return acc
    },
    {
      primaryDomains: [],
      secondaryDomains: {},
    }
  )

  const [defaultPrimaryDomain, ...customPrimaryDomains] = primaryDomains

  // to maintain unchanged order of primary domains, we order them alphabetically
  // excluding 'www.' prefix from order comparison
  const customPrimaryDomainsOrdered = customPrimaryDomains.sort((a, b) => {
    const A = a.id.replace(/^www\./, "")
    const B = b.id.replace(/^www\./, "")

    return A.localeCompare(B)
  })

  const primaryDomainsOrdered = [
    defaultPrimaryDomain,
    ...customPrimaryDomainsOrdered,
  ]

  const certificates = hostingDetails.certificates ?? []

  return (
    <div {...rest}>
      <div css={headerCss}>
        <Heading as="h4" css={headingCss}>
          {text.headers.connectedDomains}
        </Heading>
        <Button
          onClick={() => setShowDomainForm(true)}
          variant="GHOST"
          size="S"
        >
          {text.actions.addDomain}
        </Button>
      </div>
      <div css={listCss}>
        {primaryDomainsOrdered.map(primaryDomain => {
          const primaryCertificate = certificates.find(cert => {
            return cert.domains?.find(dmn => dmn.id === primaryDomain.id)
          })

          const secondaryDomain = secondaryDomains[primaryDomain.id]

          const secondaryCertificate =
            secondaryDomain &&
            certificates.find(cert => {
              return cert.domains?.find(dmn => dmn.id === secondaryDomain.id)
            })

          return (
            <PrimarySecondaryCombo
              key={primaryDomain.id}
              siteId={siteId}
              primaryDomain={primaryDomain}
              primaryCertificate={primaryCertificate}
              secondaryDomain={secondaryDomain}
              secondaryCertificate={secondaryCertificate}
              onRemove={setDomainToRemove}
              onMakePrimary={setDomainToPrimary}
              onRequestCertificate={setManageCertificateForDomain}
            />
          )
        })}
      </div>

      {!!domainToRemove && (
        <RemoveDomainForm
          siteId={siteId}
          domain={domainToRemove}
          onDismiss={() => setDomainToRemove(null)}
          onComplete={() => {
            setDomainToRemove(null)

            trackSegment({
              type: SegmentEventType.Track,
              event: "Custom Domain removed",
              properties: {
                location: "Site Settings",
              },
            })
          }}
        />
      )}

      {!!domainToPrimary && (
        <MakeDomainPrimaryForm
          siteId={siteId}
          domain={domainToPrimary}
          onDismiss={() => setDomainToPrimary(null)}
          onComplete={() => {
            setDomainToPrimary(null)

            trackSegment({
              type: SegmentEventType.Track,
              event: "Domain set as Primary",
              properties: {
                location: "Site Settings",
              },
            })
          }}
        />
      )}

      {!!manageCertificateForDomain && (
        <RequestCertificateForm
          siteId={siteId}
          domain={manageCertificateForDomain}
          onDismiss={() => setManageCertificateForDomain(null)}
          onComplete={() => {
            setManageCertificateForDomain(null)

            trackSegment({
              type: SegmentEventType.Track,
              event: "Auto SSL Certificate re-enabled",
              properties: {
                location: "Site Settings",
              },
            })
          }}
        />
      )}

      {showDomainForm && (
        <StyledPanelBoilerplate
          isOpen={showDomainForm}
          aria-label={text.headers.addDomain}
          onClose={() => setShowDomainForm(false)}
          header={text.headers.addDomain}
        >
          <StyledPanelBodySection>
            <GatsbyCloudCdnFeatures css={formDescriptionCss} />
            <AddDomainForm
              siteId={siteId}
              onComplete={() => {
                setShowDomainForm(false)

                trackSegment({
                  type: SegmentEventType.Track,
                  event: "Custom Domain added",
                  properties: {
                    location: "Site Settings",
                  },
                })
              }}
              onCancel={() => setShowDomainForm(false)}
            />
          </StyledPanelBodySection>
        </StyledPanelBoilerplate>
      )}
    </div>
  )
}
